
import React, { useMemo } from 'react';
import { Puzzle } from '../types';
import PostCard from '../components/PostCard';
import { usePuzzles } from '../PuzzleContext';
import BackButton from '../components/BackButton';
import { useAuth } from '../AuthContext';

interface FilteredFeedPageProps {
    category?: string;
    hashtag?: string;
}

const FilteredFeedPage: React.FC<FilteredFeedPageProps> = ({ category, hashtag }) => {
    const { puzzles, reportPuzzle, puzzlesLoading } = usePuzzles();
    const { userProfile, allUsers, usersLoading } = useAuth();

    const filteredPuzzles = useMemo(() => {
        if (usersLoading || puzzlesLoading || !userProfile) {
            return [];
        }

        let basePuzzles = puzzles;

        if (category === "Featured Brands") {
            basePuzzles = basePuzzles.filter(p => p.isSponsored === true);
        } else if (category) {
            basePuzzles = basePuzzles.filter(p => p.category === category);
        } else if (hashtag) {
            basePuzzles = basePuzzles.filter(p => p.hashtags?.includes(hashtag.toLowerCase()));
        } else {
            return [];
        }
        
        return basePuzzles.filter(p => {
            const creator = allUsers.find(u => u.uid === p.creatorId);
            if (!creator) {
                return false;
            }
            
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(p.creatorId);
            const theyHaveBlockedMe = creator.blockedUsers?.includes(userProfile.uid);
            
            if (iHaveBlockedThem || theyHaveBlockedMe) {
                return false;
            }
            
            return true;
        });
    }, [puzzles, category, hashtag, userProfile, allUsers, usersLoading, puzzlesLoading]);

    const feedWithAds = useMemo(() => {
        return filteredPuzzles;
    }, [filteredPuzzles]);

    const isLoading = puzzlesLoading || usersLoading || !userProfile;

    const isFeaturedBrand = ["Tech & Electronics", "Toys & Games", "Clothes & Apparel", "Footwear"].includes(category || "");
    const backText = isFeaturedBrand ? "Back to Featured Brands" : "Back to Categories";

    return (
        <div className="space-y-4">
            <BackButton text={backText} />
            <h2 id="filtered-feed-title" className="text-2xl font.bold">{category || hashtag}</h2>
            <div id="filtered-feed" className="space-y-4 pb-4">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)' }}></div>
                    </div>
                ) : feedWithAds.length > 0 ? (
                    feedWithAds.map(item => {
                        return <PostCard key={item.id} puzzle={item as Puzzle} onReport={reportPuzzle} />;
                    })
                ) : (
                    <p className="text-center text-gray-500 py-8">No puzzles found for {category || hashtag}.</p>
                )}
            </div>
        </div>
    );
};

export default FilteredFeedPage;